import { useMemo, useRef, useState } from 'react'
import { MessageSquare, Pencil, Plus, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import type { AgentThread } from '@/lib/api/agent'
import { cn } from '@/lib/utils'

export function relativeTime(value: string | null): string {
  if (!value) return ''
  const minutes = Math.round((Date.now() - new Date(value).getTime()) / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

/** The person's conversations, renamable in place. Only ever their own: the API lists no one else's. */
function ThreadList({
  threads,
  activeId,
  onSelect,
  onRename,
}: {
  threads: AgentThread[]
  activeId: string | null
  onSelect: (id: string) => void
  onRename: (id: string, title: string) => Promise<void>
}) {
  const [editing, setEditing] = useState<string | null>(null)
  const [draft, setDraft] = useState('')

  const commit = async (id: string) => {
    const title = draft.trim()
    setEditing(null)
    // An unchanged or emptied title is not worth a request.
    if (title && title !== threads.find((thread) => thread.id === id)?.title) {
      await onRename(id, title)
    }
  }

  return (
    <ul className="space-y-0.5">
      {threads.map((thread) => (
        <li key={thread.id}>
          {editing === thread.id ? (
            <input
              autoFocus
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onBlur={() => void commit(thread.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void commit(thread.id)
                if (event.key === 'Escape') setEditing(null)
              }}
              className="w-full rounded-md border border-input bg-background px-2.5 py-2 text-sm"
              aria-label="Conversation title"
            />
          ) : (
            <div
              className={cn(
                'group flex items-center gap-1 rounded-md',
                thread.id === activeId ? 'bg-accent text-accent-foreground' : 'hover:bg-muted',
              )}
            >
              <button
                type="button"
                onClick={() => onSelect(thread.id)}
                aria-current={thread.id === activeId ? 'true' : undefined}
                className="min-w-0 flex-1 px-2.5 py-2 text-left"
              >
                <span className="block truncate text-sm">{thread.title ?? 'Untitled'}</span>
                <span className="block text-xs text-muted-foreground">
                  {relativeTime(thread.last_message_at ?? thread.created_at)}
                </span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setDraft(thread.title ?? '')
                  setEditing(thread.id)
                }}
                className="shrink-0 px-2.5 text-muted-foreground hover:text-foreground"
                aria-label={`Rename ${thread.title ?? 'Untitled'}`}
              >
                <Pencil className="size-3.5" />
              </button>
            </div>
          )}
        </li>
      ))}
    </ul>
  )
}

/**
 * Every past conversation, in a drawer opened from the page header: out of the
 * way while working, a click away when needed, and the same on a phone.
 */
export function HistoryDrawer({
  open,
  onOpenChange,
  threads,
  activeId,
  onSelect,
  onNew,
  onRename,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  threads: AgentThread[]
  activeId: string | null
  onSelect: (id: string) => void
  onNew: () => void
  onRename: (id: string, title: string) => Promise<void>
}) {
  const [query, setQuery] = useState('')
  const content = useRef<HTMLDivElement>(null)
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return needle
      ? threads.filter((thread) => (thread.title ?? 'Untitled').toLowerCase().includes(needle))
      : threads
  }, [threads, query])

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        ref={content}
        side="right"
        className="w-[88vw] gap-0 p-0 sm:max-w-sm"
        // Into the drawer, but not into the search box: on a phone that would
        // raise the keyboard over the list someone opened it to read.
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          content.current?.focus()
        }}
      >
        <SheetHeader className="gap-3 border-b border-border p-4 pr-12">
          <SheetTitle className="font-display text-base">History</SheetTitle>
          <SheetDescription className="sr-only">
            Your conversations with Beacon. Only you can see them.
          </SheetDescription>
          <div className="flex items-center gap-2">
            <div className="relative min-w-0 flex-1">
              <Search
                aria-hidden
                className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search"
                aria-label="Search conversations"
                // 16px on a phone, or iOS zooms the page when it is tapped.
                className="h-8 pl-8 text-lg md:text-sm"
              />
            </div>
            <Button size="sm" onClick={onNew}>
              <Plus />
              New chat
            </Button>
          </div>
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {threads.length === 0 ? (
            <p className="px-2.5 py-6 text-center text-sm text-muted-foreground">
              No conversations yet.
            </p>
          ) : shown.length === 0 ? (
            <p className="px-2.5 py-6 text-center text-sm text-muted-foreground">
              Nothing matches “{query.trim()}”.
            </p>
          ) : (
            <ThreadList
              threads={shown}
              activeId={activeId}
              onSelect={onSelect}
              onRename={onRename}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

/** The last few conversations, under the home composer, to pick one back up. */
export function RecentConversations({
  threads,
  onSelect,
  onViewAll,
  limit = 4,
}: {
  threads: AgentThread[]
  onSelect: (id: string) => void
  onViewAll: () => void
  limit?: number
}) {
  if (threads.length === 0) return null
  return (
    <section aria-labelledby="beacon-recent" className="w-full">
      <div className="mb-1.5 flex items-baseline justify-between px-1">
        <h2 id="beacon-recent" className="text-xs font-semibold text-muted-foreground">
          Recent
        </h2>
        <button
          type="button"
          onClick={onViewAll}
          className="text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
        >
          View all
        </button>
      </div>
      <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
        {threads.slice(0, limit).map((thread) => (
          <li key={thread.id}>
            <button
              type="button"
              onClick={() => onSelect(thread.id)}
              className="flex w-full items-center gap-3 px-3.5 py-2.5 text-left transition-colors hover:bg-muted/60"
            >
              <MessageSquare aria-hidden className="size-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate text-sm">{thread.title ?? 'Untitled'}</span>
              <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                {relativeTime(thread.last_message_at ?? thread.created_at)}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}
